// src/components/Experiment/VisualizationDialog.js
import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { estimatePermutation } from '../../api/photoProcessingApi';

// '40 s', '12 min', '2.5 h'
const formatDuration = (seconds) => {
  if (seconds < 90) return `${Math.max(1, Math.round(seconds))} s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(1)} h`;
};

const VisualizationDialog = ({ show, onHide, analysis, onSubmit }) => {
  const [config, setConfig] = useState({
    name: '',
    plot_type: '',
    parameters: {}
  });
  const [errors, setErrors] = useState({});
  // Nested CV analysis: every permutation repeats the whole nested procedure
  const isNestedAnalysis = analysis?.results?.validation_mode === 'nested';
  // More than two classes: the permutation test uses balanced accuracy by default
  // (no R² / Q²; AUC (Hand & Till) for a simple analysis only)
  const isMulticlass = (analysis?.results?.classes?.length ?? 0) > 2;
  const defaultPermutationMetric = isMulticlass
    ? 'balanced_accuracy'
    : ((analysis?.analysis_type === 'plsda' && !isNestedAnalysis) ? 'r2' : 'auc');
  // Estimated time of the permutation test of a nested analysis (from the server)
  const [permEstimate, setPermEstimate] = useState(null);
  const permutationCount = config.parameters?.n_permutations ?? 200;

  useEffect(() => {
    if (!show || !analysis?.id || !isNestedAnalysis || config.plot_type !== 'permutation_plot' ||
        !Number.isFinite(permutationCount) || permutationCount < 1) {
      setPermEstimate(null);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const result = await estimatePermutation(analysis.id, permutationCount, controller.signal);
        setPermEstimate(result?.nested ? result : null);
      } catch (error) {
        if (!controller.signal.aborted) setPermEstimate(null);
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [show, analysis?.id, isNestedAnalysis, config.plot_type, permutationCount]);

  useEffect(() => {
    if (show && analysis) {
      setConfig({
        name: `${analysis.name} - Visualization`,
        plot_type: '',
        parameters: {}
      });
      setErrors({});
    }
  }, [show, analysis]);

  const getAvailableVisualizationTypes = () => {
    if (!analysis) return [];

    const allTypes = [
      { value: 'score_plot', label: 'Score Plot',
        available: ['pca', 'lda', 'plsda'] },
      { value: 'loading_plot', label: 'Loading Plot',
        available: ['pca', 'lda', 'plsda'] },
      { value: 'confusion_matrix', label: 'Confusion Matrix',
        available: ['lda', 'logistic', 'svm', 'random_forest', 'knn', 'plsda', 'simca', 'xgboost', 'bbc', 'brf'] },
      { value: 'roc_curve', label: 'ROC Curve',
        available: ['logistic', 'svm', 'random_forest', 'knn', 'plsda'] },
      { value: 'feature_importance', label: 'Feature Importance',
        available: ['random_forest'] },
      { value: 'time_series', label: 'Time Series',
        available: ['pca', 'lda', 'logistic', 'linear', 'svm', 'random_forest', 'knn', 'plsda', 'simca'] },
      { value: 'heatmap', label: 'Heatmap',
        available: ['pca', 'lda', 'logistic', 'linear', 'svm', 'random_forest', 'knn', 'plsda', 'simca'] },
      { value: 'knn_distance', label: 'KNN Distance Analysis',
        available: ['knn'] },
      { value: 'permutation_plot', label: 'Permutation Plot (Validation)',
        available: ['plsda', 'lda', 'logistic', 'svm', 'random_forest', 'simca', 'knn'],
        classificationOnly: true },
      { value: 'bootstrap_ci', label: 'Bootstrap CI (AUC, Accuracy, Sensitivity, ...)',
        available: ['plsda', 'lda', 'logistic', 'svm', 'random_forest', 'simca', 'knn'],
        binaryOnly: true, hardPlsdaOnly: true }
    ];

    // Bootstrap CI is defined for binary tasks only, the permutation plot for
    // two or more classes; bootstrap CI needs hard PLS-DA (soft PLS-DA may leave samples unassigned)
    // PLS-DA analyses run before the sanitize_for_json fix store is_binary as 1
    const isBinary = analysis.results?.is_binary === true || analysis.results?.is_binary === 1;
    const isClassification = isBinary || (analysis.results?.classes?.length ?? 0) > 2;
    const plsdaStyle = analysis.results?.model_info?.style ?? analysis.parameters?.style ?? 'hard';

    return allTypes.filter(type =>
      type.available.includes(analysis.analysis_type) &&
      (!type.binaryOnly || isBinary) &&
      (!type.classificationOnly || isClassification) &&
      !(type.hardPlsdaOnly && analysis.analysis_type === 'plsda' && plsdaStyle !== 'hard')
    );
  };

  const renderParameterFields = () => {
    switch (config.plot_type) {
      case 'score_plot':
        return (
          <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>X Component</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="10"
                    value={config.parameters.component_x ?? 0}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, component_x: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Component index (0-based)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Y Component</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="10"
                    value={config.parameters.component_y ?? 1}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, component_y: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Component index (0-based)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Font Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="8"
                    max="24"
                    step="1"
                    value={config.parameters.font_size ?? 14}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, font_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Base font size for labels and titles
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Legend Font Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="8"
                    max="20"
                    step="1"
                    value={config.parameters.legend_font_size ?? 12}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, legend_font_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Font size for legend text
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marker Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="10"
                    max="200"
                    step="10"
                    value={config.parameters.marker_size ?? 60}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, marker_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Size of scatter plot markers
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Point Transparency</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    value={config.parameters.alpha ?? 0.8}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, alpha: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Alpha value for points (0.1 = transparent, 1.0 = opaque)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Draw Confidence Ellipses"
                    checked={config.parameters.draw_confidence_ellipses ?? false}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, draw_confidence_ellipses: e.target.checked }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Draw confidence ellipses around each group
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Ellipse Confidence Level (σ)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1.0"
                    max="3.0"
                    step="0.1"
                    value={config.parameters.ellipse_std ?? 1.96}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, ellipse_std: parseFloat(e.target.value) }
                    })}
                    disabled={!config.parameters.draw_confidence_ellipses}
                  />
                  <Form.Text className="text-muted">
                    Standard deviations for ellipse (1.96 = 95% CI)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </>
        );

      case 'loading_plot':
        return (
          <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>X Component</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="10"
                    value={config.parameters.component_x ?? 0}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, component_x: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Component index (0-based)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Y Component</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="10"
                    value={config.parameters.component_y ?? 1}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, component_y: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Component index (0-based)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Font Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="8"
                    max="24"
                    step="1"
                    value={config.parameters.font_size ?? 12}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, font_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Base font size for labels and titles
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marker Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="10"
                    max="200"
                    step="10"
                    value={config.parameters.marker_size ?? 40}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, marker_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Size of loading point markers
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Point Transparency</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    value={config.parameters.alpha ?? 0.8}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, alpha: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Alpha value for points (0.1 = transparent, 1.0 = opaque)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Top N Features</Form.Label>
                  <Form.Control
                    type="number"
                    min="5"
                    max="100"
                    value={config.parameters.top_n_features ?? null}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: {
                        ...config.parameters,
                        top_n_features: e.target.value ? parseInt(e.target.value) : null
                      }
                    })}
                    placeholder="All features"
                  />
                  <Form.Text className="text-muted">
                    Show only top N most important features (leave empty for all)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Show Feature Labels"
                    checked={config.parameters.show_labels ?? true}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, show_labels: e.target.checked }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Display feature names on the plot
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Show Vector Arrows"
                    checked={config.parameters.show_vectors ?? true}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, show_vectors: e.target.checked }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Show arrows from origin to loading points
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Show Correlation Circles"
                    checked={config.parameters.show_correlation_circles ?? false}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, show_correlation_circles: e.target.checked }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Show correlation reference circles (r=0.5, r=1.0)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </>
        );

      case 'time_series':
        return (
          <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Grouping Mode</Form.Label>
                  <Form.Select
                    value={config.parameters.grouping_mode ?? 'sample'}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, grouping_mode: e.target.value }
                    })}
                  >
                    <option value="sample">By Sample</option>
                    <option value="class">By Classification Group {analysis.label_column}</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Group data by individual samples or classification groups
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Line Width</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="5"
                    step="0.5"
                    value={config.parameters.line_width ?? 2}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, line_width: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Width of time series lines
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marker Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="3"
                    max="15"
                    step="1"
                    value={config.parameters.marker_size ?? 6}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, marker_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Size of data point markers
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Point Transparency</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    value={config.parameters.alpha ?? 0.8}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, alpha: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Alpha value for lines and markers
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Show Error Bars"
                    checked={config.parameters.show_error_bars ?? true}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, show_error_bars: e.target.checked }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Show standard deviation as shaded area around mean
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Error Bar Alpha</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    value={config.parameters.error_alpha ?? 0.2}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, error_alpha: parseFloat(e.target.value) }
                    })}
                    disabled={!config.parameters.show_error_bars}
                  />
                  <Form.Text className="text-muted">
                    Transparency of error bar shaded areas
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Show Grid"
                    checked={config.parameters.show_grid ?? true}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, show_grid: e.target.checked }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Display grid lines in the plot
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Font Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="8"
                    max="24"
                    step="1"
                    value={config.parameters.font_size ?? 12}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, font_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Base font size for labels and titles
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Legend Font Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="8"
                    max="20"
                    step="1"
                    value={config.parameters.legend_font_size ?? 9}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, legend_font_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Font size for legend text
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>X-axis Rotation</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="90"
                    step="5"
                    value={config.parameters.x_rotation ?? 45}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, x_rotation: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Rotation angle for x-axis labels (degrees)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </>
        );

      case 'confusion_matrix':
        return (
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Predictions</Form.Label>
                <Form.Select
                  value={config.parameters.source ?? 'cv'}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...config.parameters, source: e.target.value }
                  })}
                >
                  <option value="cv">Cross-validation (pooled out-of-fold)</option>
                  <option value="training">Training data (self-test)</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Cross-validation: every sample is predicted by a model that did not see it — the matrix
                  to report. Training: the final model on its own training data (optimistic).
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Show row percentages"
                  checked={config.parameters.show_percentages ?? false}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...config.parameters, show_percentages: e.target.checked }
                  })}
                />
                <Form.Text className="text-muted">
                  Adds the share of the true class to every cell (the diagonal is the recall of the class)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        );

      case 'heatmap':
        return (
          <Row>
            <Col>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Normalize data"
                  checked={config.parameters.normalize ?? true}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...config.parameters, normalize: e.target.checked }
                  })}
                />
                <Form.Text className="text-muted">
                  Normalize features to zero mean and unit variance
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        );

      case 'knn_distance':
        return (
          <Row>
            <Col>
              <Form.Group>
                <Form.Label>Number of Neighbors to Display</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  max="20"
                  value={config.parameters.n_neighbors ?? 5}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...config.parameters, n_neighbors: parseInt(e.target.value) }
                  })}
                />
                <Form.Text className="text-muted">
                  Number of nearest neighbors to include in distance analysis
                </Form.Text>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Max Samples to Display</Form.Label>
                <Form.Control
                  type="number"
                  min="10"
                  max="100"
                  value={config.parameters.max_samples ?? 50}
                  onChange={(e) => setConfig({
                    ...config,
                    parameters: { ...config.parameters, max_samples: parseInt(e.target.value) }
                  })}
                />
                <Form.Text className="text-muted">
                  Maximum number of samples to show in distance matrix
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        );

      case 'permutation_plot':
        return (
          <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Metric</Form.Label>
                  <Form.Select
                    value={config.parameters.metric ?? defaultPermutationMetric}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, metric: e.target.value }
                    })}
                  >
                    {isMulticlass && (
                      <>
                        <option value="balanced_accuracy">Balanced Accuracy</option>
                        <option value="accuracy">Accuracy</option>
                        {/* the nested engine computes no multi-class AUC */}
                        {!isNestedAnalysis && <option value="auc">AUC (Hand &amp; Till)</option>}
                      </>
                    )}
                    {!isMulticlass && analysis.analysis_type === 'plsda' && !isNestedAnalysis && (
                      <>
                        <option value="r2">R² / Q²</option>
                        <option value="accuracy">Accuracy</option>
                        <option value="auc">AUC</option>
                      </>
                    )}
                    {/* Q² is not computed for nested analyses */}
                    {!isMulticlass && (['lda', 'logistic', 'svm', 'random_forest', 'simca', 'knn'].includes(analysis.analysis_type) ||
                      (analysis.analysis_type === 'plsda' && isNestedAnalysis)) && (
                      <>
                        <option value="auc">AUC</option>
                        <option value="accuracy">Accuracy</option>
                      </>
                    )}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {isMulticlass
                      ? `${analysis.results.classes.length} classes: balanced accuracy is recommended (it does not favour the larger classes).`
                      : 'Choose the metric to plot for the permutation test.'}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Permutations</Form.Label>
                  <Form.Control
                    type="number"
                    min="50"
                    max="500"
                    step="10"
                    value={config.parameters.n_permutations ?? 200}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, n_permutations: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Number of label shuffles (higher = more stable, but slower)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {isNestedAnalysis && (
              <Row>
                <Col>
                  <Alert variant="info" className="py-2">
                    <div>
                      <i className="bi bi-info-circle me-2"></i>
                      Nested CV analysis: every permutation repeats the whole nested procedure
                      (feature selection and the choice inside every fold), so the test takes much
                      longer than for a simple analysis. The train series is the final model
                      (the variant chosen on all the data) scored on its training data.
                    </div>
                    {permEstimate && (
                      <div className="mt-1">
                        <strong>
                          Estimated: about {formatDuration(permEstimate.wall_seconds[0])}&ndash;{formatDuration(permEstimate.wall_seconds[1])}
                          {' '}for {permEstimate.n_permutations} permutations
                        </strong>
                      </div>
                    )}
                    <Form.Check
                      type="checkbox"
                      id="permutation-early-stop"
                      className="mt-2"
                      label="Stop early when the result is clearly not significant"
                      checked={!!config.parameters.early_stop}
                      onChange={(e) => setConfig({
                        ...config,
                        parameters: { ...config.parameters, early_stop: e.target.checked }
                      })}
                    />
                    <Form.Text className="text-muted">
                      The test stops once 20 permutations are not worse than the original labels
                      (p = 20 / permutations done). Significant results always run to the end.
                    </Form.Text>
                  </Alert>
                </Col>
              </Row>
            )}

            {/* Same rule as the backend (get_feature_selection_params): off when enabled === false or k === 'all' */}
            {!isNestedAnalysis && (() => {
              const fs = analysis.parameters?.feature_selection;
              const fsK = fs?.k ?? 'all';
              const fsUsed = !!fs && fs.enabled !== false && fsK !== 'all';
              return (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Feature Selection in Permutations</Form.Label>
                      <Form.Select
                        value={config.parameters.feature_selection_mode ?? 'per_permutation'}
                        disabled={!fsUsed}
                        onChange={(e) => setConfig({
                          ...config,
                          parameters: { ...config.parameters, feature_selection_mode: e.target.value }
                        })}
                      >
                        <option value="per_permutation">Repeated for each permutation (recommended)</option>
                        <option value="once">Once, on original labels (as in analysis)</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        {fsUsed
                          ? `The analysis selected k = ${fsK} features. "Repeated" re-selects them for every shuffle, so the original model and the permuted ones go through the same procedure (fair p-value, slower). "Once" is faster but makes the original model look better than it is.`
                          : 'Not applicable: this analysis did not use feature selection.'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              );
            })()}

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Marker Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="10"
                    max="200"
                    step="5"
                    value={config.parameters.marker_size ?? 40}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, marker_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Size of scatter markers for permuted points
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Font Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="8"
                    max="24"
                    step="1"
                    value={config.parameters.font_size ?? 14}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, font_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Base font size for labels and titles
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Legend Font Size</Form.Label>
                  <Form.Control
                    type="number"
                    min="8"
                    max="20"
                    step="1"
                    value={config.parameters.legend_font_size ?? 12}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, legend_font_size: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Font size for legend text
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Point Transparency</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.1"
                    max="1.0"
                    step="0.1"
                    value={config.parameters.alpha ?? 0.5}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, alpha: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Alpha for scatter points (0.1 = transparent, 1.0 = opaque)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Regression Line Width</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.5"
                    max="3.0"
                    step="0.5"
                    value={config.parameters.linewidth ?? 1.5}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, linewidth: parseFloat(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Width of regression lines
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Show Train Metric (R² / Accuracy / AUC)"
                    checked={config.parameters.show_train ?? true}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, show_train: e.target.checked }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Also plot the train metric (e.g., R², accuracy, AUC) in addition to the cross‑validated metric
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </>
        );

      case 'bootstrap_ci':
        return (
          <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Iterations</Form.Label>
                  <Form.Control
                    type="number"
                    min="200"
                    max="10000"
                    step="100"
                    value={config.parameters.n_iterations ?? 2000}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, n_iterations: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    Bootstrap resamples, 200–10000 (higher = more stable interval, but slower)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Random Seed</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    value={config.parameters.random_state ?? 42}
                    onChange={(e) => setConfig({
                      ...config,
                      parameters: { ...config.parameters, random_state: parseInt(e.target.value) }
                    })}
                  />
                  <Form.Text className="text-muted">
                    With few samples the lower bound depends on the seed: report it together with the interval
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Same rule as the backend (get_feature_selection_params): off when enabled === false or k === 'all' */}
            {(() => {
              const fs = analysis.parameters?.feature_selection;
              // Nested analysis (k: 'auto'): the number of features of its final model
              const fsK = (fs?.k === 'auto' ? analysis.results?.feature_selection?.k : fs?.k) ?? 'all';
              const fsUsed = !!fs && fs.enabled !== false && fsK !== 'all';
              return (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Feature Selection in Iterations</Form.Label>
                      <Form.Select
                        value={config.parameters.feature_selection_mode ?? 'per_iteration'}
                        disabled={!fsUsed}
                        onChange={(e) => setConfig({
                          ...config,
                          parameters: { ...config.parameters, feature_selection_mode: e.target.value }
                        })}
                      >
                        <option value="per_iteration">Repeated in each iteration (recommended)</option>
                        <option value="once">Once, on all data (as in analysis)</option>
                      </Form.Select>
                      <Form.Text className="text-muted">
                        {fsUsed
                          ? `The analysis selected k = ${fsK} features. "Repeated" selects them on the training part of every iteration, so the out-of-bag samples never influence the selection. "Once" is faster but makes the interval optimistic.`
                          : 'Not applicable: this analysis did not use feature selection.'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              );
            })()}

            {['svm', 'random_forest'].includes(analysis.analysis_type) && (
              <Alert variant="warning" className="mb-0">
                <small>
                  The model is re-trained in every iteration. For {analysis.analysis_type === 'svm' ? 'SVM' : 'Random Forest'} this
                  can take from minutes to tens of minutes; start with fewer iterations.
                </small>
              </Alert>
            )}
          </>
        );

      default:
        return (
          <Alert variant="info" className="mt-2">
            <small>No additional parameters required for this plot type.</small>
          </Alert>
        );
    }
  };

  const getPlotDescription = () => {
    const descriptions = {
      'score_plot': 'Shows the projection of samples onto principal components or discriminant axes. Useful for visualizing sample clustering and separation. Enhanced with scientific journal formatting options.',
      'loading_plot': 'Displays the contribution of original features to principal components. Helps identify which features drive the observed patterns.',
      'confusion_matrix': 'Visualizes classification performance by showing actual vs predicted class labels. Diagonal elements represent correct classifications. By default the matrix of the cross-validation (pooled out-of-fold predictions) is drawn, in counts; samples that SIMCA or soft PLS-DA assign to no class are shown in an extra "Unassigned" column.',
      'roc_curve': 'Receiver Operating Characteristic curve showing the trade-off between true positive rate and false positive rate across different classification thresholds.',
      'feature_importance': 'Ranks features by their contribution to the model. Higher values indicate more important features for prediction.',
      'time_series': 'Shows how color intensity values change over time for different samples and channels. Useful for tracking kinetic behavior.',
      'heatmap': 'Displays a color-coded matrix of feature values across samples. Useful for identifying patterns and clusters in high-dimensional data.',
      'knn_distance': 'Analyzes distances between samples in K-Nearest Neighbors space. Shows sample similarity and potential outliers.',
      'permutation_plot': 'Validates the model by repeatedly shuffling the class labels and re-evaluating it. Compares the original metric (train and cross‑validated) with the distribution obtained from shuffled data. A valid model shows the original CV metric clearly above the permuted cloud and a low p‑value; a model that mostly memorises the training set shows a large gap between the train and CV metrics. If the analysis used feature selection, it can be applied once on the original labels or repeated for every permutation. For nested CV analyses every permutation repeats the whole nested procedure.',
      'bootstrap_ci': 'Estimates how stable the model is: the model is re-trained on stratified bootstrap resamples of the samples and evaluated on the samples left out (out-of-bag). Shows the distribution of AUC and accuracy and the 95% percentile interval, next to the CV metric of the analysis and its analytical interval; the intervals of sensitivity, specificity, balanced accuracy, precision and F1 are reported as numbers. Replicates of one sample are always resampled together. With very few samples the lower bound depends on the random seed — report the seed together with the interval.'
    };

    return descriptions[config.plot_type] || 'Visualization of analysis results.';
  };

  const validateConfig = () => {
    const newErrors = {};
    if (!config.name.trim()) {
      newErrors.name = 'Visualization name is required';
    }
    if (!config.plot_type) {
      newErrors.plot_type = 'Plot type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateConfig()) {
      onSubmit(config);
    }
  };

  if (!analysis) return null;

  const availableTypes = getAvailableVisualizationTypes();

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-graph-up me-2"></i>
          Create Visualization
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <strong>Analysis:</strong> {analysis.name} ({analysis.analysis_type.toUpperCase()})
          <br />
          <strong>Available plots:</strong> {availableTypes.length} types
        </Alert>

        <Form>
          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Visualization Name</Form.Label>
                <Form.Control
                  type="text"
                  value={config.name}
                  onChange={(e) => setConfig({ ...config, name: e.target.value })}
                  isInvalid={!!errors.name}
                  placeholder="Enter a descriptive name for this visualization"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Plot Type</Form.Label>
                <Form.Select
                  value={config.plot_type}
                  onChange={(e) => {
                    const newPlotType = e.target.value;
                    let defaultParams = {};
                    if (newPlotType === 'permutation_plot') {
                      defaultParams = { metric: defaultPermutationMetric };
                    }
                    if (newPlotType === 'confusion_matrix') {
                      // Sent explicitly: without 'source' the backend draws the training
                      // matrix (visualizations created before the option existed)
                      defaultParams = { source: 'cv', show_percentages: false };
                    }
                    setConfig({
                      ...config,
                      plot_type: newPlotType,
                      parameters: defaultParams
                    });
                  }}
                  isInvalid={!!errors.plot_type}
                >
                  <option value="">Select plot type...</option>
                  {availableTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.plot_type}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Choose the type of visualization to generate
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {config.plot_type && (
            <Alert variant="light" className="mb-3">
              <strong>Description:</strong> {getPlotDescription()}
            </Alert>
          )}

          {config.plot_type && (
            <div className="mb-3">
              <h6>Plot Parameters</h6>
              {renderParameterFields()}
            </div>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          <i className="bi bi-plus-circle me-1"></i>
          Create Visualization
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VisualizationDialog;